import { NavLink, Link, useNavigate } from 'react-router-dom';
import { signOut, linkWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';
import { useState, useEffect, useRef } from 'react';

export default function Navbar({ userRole }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Explicitly check and set admin status
    setIsAdmin(userRole === 'admin');

    // Check if Google is linked
    const user = auth.currentUser;
    if (user) {
      setIsGoogleLinked(
        user.providerData.some((p) => p.providerId === 'google.com')
      );
    }
  }, [userRole]);

  useEffect(() => {
    // Close menu when clicking outside
    function handleClickOutside(event) {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, buttonRef]);

  const handleLogout = async () => {
    setIsMenuOpen(false);
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(prevState => !prevState);
  };

  const handleConnectGoogle = async () => {
    setLinkingGoogle(true);
    try {
      await linkWithPopup(auth.currentUser, googleProvider);
      setIsGoogleLinked(true);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        console.error('Failed to link Google:', err);
      }
    } finally {
      setLinkingGoogle(false);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-content">
          <Link to="/" className="navbar-brand">
            <img src="/logo-white.png" alt="SoHera_" className="navbar-logo" />
            <span className="navbar-brand-sub">Invoice System</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="navbar-links">
            <NavLink to="/" end className="navbar-link">
              Dashboard
            </NavLink>
            <NavLink to="/invoices" className="navbar-link">
              Invoices
            </NavLink>
            <NavLink to="/create-invoice" className="navbar-link">
              Create Invoice
            </NavLink>
            <NavLink to="/clients" className="navbar-link">
              Clients
            </NavLink>
            <NavLink to="/receipts" className="navbar-link">
              Receipts
            </NavLink>
            {isAdmin && (
              <NavLink to="/users" className="navbar-link">
                Users
              </NavLink>
            )}
            <NavLink to="/profile" className="navbar-link">
              Profile
            </NavLink>
            {!isGoogleLinked && (
              <button
                onClick={handleConnectGoogle}
                className="navbar-button"
                style={{ background: 'rgba(255,255,255,0.16)', color: 'white' }}
                disabled={linkingGoogle}
              >
                {linkingGoogle ? 'Connecting...' : 'Connect Google'}
              </button>
            )}
            <button
              onClick={handleLogout}
              className="navbar-button"
            >
              Logout
            </button>
            <a
              href="https://www.sohera.co.za/"
              className="navbar-link"
              target="_blank"
              rel="noreferrer"
            >
              Back to SoHera
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            ref={buttonRef}
            onClick={toggleMenu}
            className={`navbar-menu-button ${isMenuOpen ? 'open' : ''}`}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <div className="hamburger-icon">
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      <div 
        ref={menuRef}
        className={`navbar-mobile ${isMenuOpen ? 'open' : ''}`}
      >
        <NavLink
          to="/"
          end
          className="navbar-mobile-link"
          onClick={() => setIsMenuOpen(false)}
        >
          Dashboard
        </NavLink>
        <NavLink
          to="/invoices"
          className="navbar-mobile-link"
          onClick={() => setIsMenuOpen(false)}
        >
          Invoices
        </NavLink>
        <NavLink
          to="/create-invoice"
          className="navbar-mobile-link"
          onClick={() => setIsMenuOpen(false)}
        >
          Create Invoice
        </NavLink>
        <NavLink
          to="/clients"
          className="navbar-mobile-link"
          onClick={() => setIsMenuOpen(false)}
        >
          Clients
        </NavLink>
        <NavLink
          to="/receipts"
          className="navbar-mobile-link"
          onClick={() => setIsMenuOpen(false)}
        >
          Receipts
        </NavLink>
        {isAdmin && (
          <NavLink
            to="/users"
            className="navbar-mobile-link"
            onClick={() => setIsMenuOpen(false)}
          >
            Users
          </NavLink>
        )}
        <NavLink
          to="/profile"
          className="navbar-mobile-link"
          onClick={() => setIsMenuOpen(false)}
        >
          Profile
        </NavLink>
        {!isGoogleLinked && (
          <button
            onClick={() => { handleConnectGoogle(); setIsMenuOpen(false); }}
            className="navbar-mobile-logout"
            style={{ background: 'rgba(255,255,255,0.16)' }}
            disabled={linkingGoogle}
          >
            {linkingGoogle ? 'Connecting...' : 'Connect Google'}
          </button>
        )}
        <button
          onClick={handleLogout}
          className="navbar-mobile-logout"
        >
          Logout
        </button>
        <a
          href="https://www.sohera.co.za/"
          className="navbar-mobile-link"
          target="_blank"
          rel="noreferrer"
          onClick={() => setIsMenuOpen(false)}
        >
          Back to SoHera
        </a>
      </div>
    </nav>
  );
}